/**
 * 合図の音（現像タイマーの攪拌音・完了音）
 *
 * iPhone の Safari は振動（Vibration API）に対応していないため、暗室での合図は音が頼り。
 * - AudioContext はタップなどの操作の中で作って起こす（unlockAudio）。操作の外で作ると
 *   iOS では鳴らないことがある
 * - iOS 16.4 以降は navigator.audioSession.type = 'playback' にすると、消音スイッチが
 *   オンでも鳴らせる（対応していない端末では何もしない）
 */

let ctx = null;

/** タップなどの操作の中で呼ぶ */
export function unlockAudio() {
  try {
    if (navigator.audioSession && navigator.audioSession.type !== 'playback') {
      navigator.audioSession.type = 'playback';
    }
  } catch {}
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    // 無音を一度鳴らして、出力を有効にしておく
    const b = ctx.createBuffer(1, 1, 22050);
    const s = ctx.createBufferSource();
    s.buffer = b;
    s.connect(ctx.destination);
    s.start(0);
  } catch {}
  return !!ctx;
}

/**
 * 合図の音量（設定）。iPhone の小さなスピーカーでも聞こえるよう、
 * 高め（1〜1.5kHz）の音を輪郭のはっきりした波形で鳴らす。
 *   normal：三角波 / loud：矩形波（既定）/ max：矩形波・音量いっぱい
 * 合図は 1 音ずつ順に鳴らし重ならないので、振幅を 1.0 未満に収めれば音割れしない。
 */
const LEVELS = {
  normal: { type: 'triangle', gain: 0.5 }, // 以前の音より約 +7dB
  loud: { type: 'square', gain: 0.5 },     // 約 +10dB（既定）
  max: { type: 'square', gain: 0.95 },     // 約 +16dB
};

export function beepLevel() {
  try {
    const v = localStorage.getItem('zn.settings.beepVolume');
    return LEVELS[v] ? v : 'loud';
  } catch { return 'loud'; }
}
export function setBeepLevel(v) {
  try { localStorage.setItem('zn.settings.beepVolume', LEVELS[v] ? v : 'loud'); } catch {}
}

function output() {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx.destination;
}

/** 1 音。at は開始までの秒 */
export function beep(freq, dur, at = 0) {
  try {
    const dest = output();
    const lv = LEVELS[beepLevel()];
    const t0 = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = lv.type;
    osc.frequency.value = freq;
    // 立ち上がりと終わりを数ミリ秒なめらかにして、プツッという雑音を防ぐ
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(lv.gain, t0 + 0.008);
    gain.gain.setValueAtTime(lv.gain, t0 + dur - 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain);
    gain.connect(dest);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  } catch {}
}

/** 攪拌：「ピピッ」（1.2kHz を 2 回） */
export function agitationBeep() {
  beep(1200, 0.12, 0);
  beep(1200, 0.12, 0.18);
}
/** 完了：「ピー・ピー・ピー」（1.5kHz を 3 回） */
export function completeBeep() {
  beep(1500, 0.4, 0);
  beep(1500, 0.4, 0.55);
  beep(1500, 0.4, 1.1);
}

/** 「音を試す」：攪拌音 → 完了音 */
export function testSound() {
  unlockAudio();
  agitationBeep();
  setTimeout(completeBeep, 700);
}

/** 消音スイッチを無視できる端末か（説明文の出し分け用） */
export function canIgnoreSilentSwitch() {
  return !!navigator.audioSession;
}
